import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Trash2, MapPin, Search } from "lucide-react";
import { MapContainer, TileLayer, Circle, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, formatApiError } from "@/lib/api";

// Fix Leaflet default icon paths (CRA doesn't bundle the static assets correctly)
const iconRetinaUrl = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png";
const iconUrl = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png";
const shadowUrl = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png";
L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });

async function searchCity(query) {
    if (!query || query.length < 2) return [];
    const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&type=municipality&limit=8`;
    const { data } = await axios.get(url);
    return (data?.features || []).map((f) => ({
        name: f.properties.city || f.properties.label,
        label: f.properties.label,
        postcode: f.properties.postcode,
        lat: f.geometry.coordinates[1],
        lon: f.geometry.coordinates[0],
    }));
}

export default function VillesForm({ mode = "edit", onSubmitted }) {
    const [cities, setCities] = useState([]);
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const debounceRef = useRef(null);

    useEffect(() => {
        api.get("/formateur/villes")
            .then((r) => setCities(r.data?.cities || []))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!query.trim()) {
            setSuggestions([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await searchCity(query);
                setSuggestions(res);
                setShowSuggestions(true);
            } catch {
                setSuggestions([]);
            }
        }, 250);
        return () => clearTimeout(debounceRef.current);
    }, [query]);

    const addCity = (s) => {
        if (cities.length >= 10) {
            toast.info("Maximum 10 villes.");
            return;
        }
        if (cities.some((c) => c.name === s.name && c.postcode === s.postcode)) {
            toast.info("Cette ville est déjà dans votre liste.");
            return;
        }
        setCities((cs) => [...cs, {
            name: s.name,
            postcode: s.postcode || null,
            lat: s.lat,
            lon: s.lon,
            radius_km: 50,
        }]);
        setQuery("");
        setSuggestions([]);
        setShowSuggestions(false);
    };

    const removeCity = (i) => setCities((cs) => cs.filter((_, idx) => idx !== i));
    const updateRadius = (i, v) => {
        setCities((cs) => cs.map((c, idx) => idx === i ? { ...c, radius_km: v } : c));
    };

    const mapCenter = useMemo(() => {
        if (cities.length === 0) return [46.603354, 1.888334]; // center of France
        const lat = cities.reduce((s, c) => s + c.lat, 0) / cities.length;
        const lon = cities.reduce((s, c) => s + c.lon, 0) / cities.length;
        return [lat, lon];
    }, [cities]);

    const save = async () => {
        if (cities.length === 0) {
            toast.error("Ajoutez au moins une ville.");
            return;
        }
        setSaving(true);
        try {
            await api.put("/formateur/villes", { cities });
            toast.success("Villes enregistrées");
            onSubmitted?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="py-20 text-center text-brand-smoke">Chargement…</div>;

    return (
        <div className="grid gap-8 lg:grid-cols-12" data-testid="villes-form">
            <div className="lg:col-span-6">
                <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium md:p-8">
                    <h3 className="font-display text-2xl">Ajouter une ville</h3>
                    <p className="mt-2 text-sm text-brand-smoke">
                        Recherchez une ville française (API gouv) et ajoutez-la à votre liste.
                    </p>

                    <div className="relative mt-5">
                        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                        <input
                            type="text"
                            placeholder="Ex : Paris, Lyon, Marseille…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onFocus={() => setShowSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 180)}
                            data-testid="city-search-input"
                            className="w-full rounded-xl border border-brand-line bg-white py-3 pl-11 pr-4 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                        />
                        {showSuggestions && suggestions.length > 0 && (
                            <ul
                                data-testid="city-suggestions"
                                className="absolute left-0 right-0 top-full z-10 mt-2 max-h-72 overflow-auto rounded-2xl border border-brand-line bg-white shadow-premium-lg"
                            >
                                {suggestions.map((s) => (
                                    <li key={`${s.name}-${s.postcode}-${s.lat}`}>
                                        <button
                                            type="button"
                                            onMouseDown={() => addCity(s)}
                                            data-testid={`city-suggestion-${s.postcode}`}
                                            className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm hover:bg-brand-orange/5"
                                        >
                                            <MapPin className="h-4 w-4 text-brand-orange" />
                                            <span>
                                                <span className="font-medium text-brand-ink">{s.name}</span>
                                                <span className="ml-2 text-xs text-brand-smoke">{s.postcode}</span>
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="mt-8 space-y-3">
                        {cities.length === 0 && (
                            <div className="rounded-xl border border-dashed border-brand-line p-6 text-center text-sm text-brand-smoke">
                                Aucune ville ajoutée. Commencez par en rechercher une ci-dessus.
                            </div>
                        )}
                        {cities.map((c, i) => (
                            <div
                                key={`${c.name}-${i}`}
                                data-testid={`city-card-${i}`}
                                className="rounded-2xl border border-brand-line bg-[#FAFAFA] p-5"
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="font-display text-lg text-brand-ink">{c.name}</div>
                                        {c.postcode && <div className="text-xs text-brand-smoke">{c.postcode}</div>}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeCity(i)}
                                        data-testid={`remove-city-${i}`}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line text-brand-smoke hover:border-red-300 hover:text-red-600"
                                        aria-label="Supprimer"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="mt-4">
                                    <div className="flex items-center justify-between text-xs text-brand-smoke">
                                        <span>Rayon d'intervention</span>
                                        <span className="font-semibold text-brand-orange">{c.radius_km} km</span>
                                    </div>
                                    <input
                                        type="range"
                                        min={10}
                                        max={200}
                                        step={10}
                                        value={c.radius_km}
                                        onChange={(e) => updateRadius(i, parseInt(e.target.value, 10))}
                                        data-testid={`city-radius-${i}`}
                                        className="mt-2 w-full accent-brand-orange"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            type="button"
                            onClick={save}
                            disabled={saving || cities.length === 0}
                            data-testid="villes-submit"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#d9772c] disabled:opacity-60"
                        >
                            {saving ? "Enregistrement…" : (<><Save className="h-4 w-4" /> {mode === "onboarding" ? "Valider mes villes" : "Enregistrer"}</>)}
                        </button>
                    </div>
                </div>
            </div>

            <div className="lg:col-span-6">
                <div className="overflow-hidden rounded-2xl border border-brand-line bg-white shadow-premium" data-testid="villes-map">
                    <div className="h-[520px] w-full">
                        <MapContainer
                            key={`${cities.length}-${mapCenter[0]}-${mapCenter[1]}`}
                            center={mapCenter}
                            zoom={cities.length === 0 ? 5 : cities.length === 1 ? 8 : 6}
                            scrollWheelZoom={true}
                            style={{ height: "100%", width: "100%" }}
                        >
                            <TileLayer
                                attribution='&copy; OpenStreetMap'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            {cities.map((c, i) => (
                                <div key={`${c.name}-${i}`}>
                                    <Marker position={[c.lat, c.lon]}>
                                        <Popup>{c.name} {c.postcode ? `(${c.postcode})` : ""}</Popup>
                                    </Marker>
                                    <Circle
                                        center={[c.lat, c.lon]}
                                        radius={c.radius_km * 1000}
                                        pathOptions={{
                                            color: "#E8873C",
                                            fillColor: "#E8873C",
                                            fillOpacity: 0.15,
                                            weight: 2,
                                        }}
                                    />
                                </div>
                            ))}
                        </MapContainer>
                    </div>
                </div>
            </div>
        </div>
    );
}
